import { z } from "zod";
import { parseCsv } from "./customers";

/* Rechnungen aus einem anderen Programm (z. B. sevDesk-Export als CSV) übernehmen. Beträge in ganzen Cent. */

const date = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Bitte ein Datum angeben.");

export const importedInvoiceSchema = z
  .object({
    number: z.string().trim().min(1).max(40).regex(/^\D*\d+$/, "Die Rechnungsnummer muss mit Ziffern enden, z. B. RE-4039."),
    issueDate: date,
    dueDate: date.nullable().optional(),
    customerNumber: z.number().int().positive().nullable().optional(),
    customerName: z.string().trim().max(160).nullable().optional(),
    netCents: z.number().int().min(1).max(1_000_000_000_00),
    grossCents: z.number().int().min(1).max(1_000_000_000_00),
    paid: z.boolean().default(false),
    paidOn: date.nullable().optional(),
    subject: z.string().trim().max(200).nullable().optional(),
  })
  .refine((v) => v.grossCents >= v.netCents, { message: "Brutto darf nicht kleiner als Netto sein." });
export type ImportedInvoice = z.input<typeof importedInvoiceSchema>;

export const invoiceImportSchema = z.object({
  invoices: z.array(importedInvoiceSchema).min(1, "Es gibt nichts zu übernehmen.").max(2000, "Bitte höchstens 2000 Rechnungen auf einmal."),
});

export interface InvoiceImportResult {
  created: number;
  skipped: { number: string; reason: string }[];
}

type Field = "number" | "issueDate" | "dueDate" | "customerNumber" | "customerName" | "net" | "gross" | "status" | "paidOn" | "subject";

const HEADER_WORDS: Record<Field, string[]> = {
  number: ["rechnungsnummer", "rechnungs-nr", "rechnungsnr", "rechnungs-nr.", "belegnummer", "nummer", "re-nr", "re-nr."],
  issueDate: ["rechnungsdatum", "belegdatum", "datum", "ausgestellt am"],
  dueDate: ["fällig am", "fälligkeitsdatum", "fälligkeit", "fällig", "zahlungsziel", "zahlbar bis"],
  customerNumber: ["kundennummer", "kundennr", "kunden-nr", "kunden-nr."],
  customerName: ["kunde", "kundenname", "organisation", "firma", "empfänger", "name"],
  net: ["netto", "nettobetrag", "betrag netto", "summe netto", "gesamt netto"],
  gross: ["brutto", "bruttobetrag", "betrag brutto", "summe brutto", "gesamt brutto", "gesamtbetrag", "betrag"],
  status: ["status", "zahlstatus", "zahlungsstatus"],
  paidOn: ["bezahlt am", "zahldatum", "zahlungsdatum", "zahlungseingang"],
  subject: ["betreff", "bezeichnung", "beschreibung", "titel"],
};

/** „1.234,56“, „1234.56“, „1 234,56 €“ → ganze Cent, sonst null. */
export function centsFromText(raw: string): number | null {
  let t = raw.replace(/[€\s ]|EUR/gi, "");
  if (!t) return null;
  const neg = t.startsWith("-");
  t = t.replace(/^[-+]/, "");
  if (/,/.test(t)) t = t.replace(/\./g, "").replace(",", ".");
  else if ((t.match(/\./g)?.length ?? 0) > 1) t = t.replace(/\./g, "");
  if (!/^\d+(\.\d{1,2})?$/.test(t)) return null;
  const [a, b = ""] = t.split(".");
  const cents = Number(a) * 100 + Number(b.padEnd(2, "0"));
  return neg ? -cents : cents;
}

/** „25.10.2026“, „25.10.26“ oder „2026-10-25“ → JJJJ-MM-TT, sonst null. */
export function isoDateFromText(raw: string): string | null {
  const t = raw.trim();
  let y: string;
  let mo: string;
  let d: string;
  const iso = t.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (iso) {
    y = iso[1]!;
    mo = iso[2]!;
    d = iso[3]!;
  } else {
    const de = t.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4}|\d{2})(?!\d)/);
    if (!de) return null;
    d = de[1]!.padStart(2, "0");
    mo = de[2]!.padStart(2, "0");
    y = de[3]!.length === 2 ? `20${de[3]}` : de[3]!;
  }
  const dt = new Date(`${y}-${mo}-${d}T12:00:00Z`);
  return dt.getUTCFullYear() === Number(y) && dt.getUTCMonth() + 1 === Number(mo) && dt.getUTCDate() === Number(d) ? `${y}-${mo}-${d}` : null;
}

export interface CsvInvoice {
  line: number;
  invoice: ImportedInvoice;
}
export interface CsvInvoiceResult {
  invoices: CsvInvoice[];
  skipped: { line: number; reason: string }[];
  recognised: string[];
  ignored: string[];
  /** Pflichtspalten, die in der Datei nicht gefunden wurden. */
  missing: string[];
}

/** Wandelt eine CSV-Datei (z. B. Rechnungsliste aus sevDesk) in zu übernehmende Rechnungen um. */
export function invoicesFromCsv(text: string): CsvInvoiceResult {
  const rows = parseCsv(text);
  const result: CsvInvoiceResult = { invoices: [], skipped: [], recognised: [], ignored: [], missing: [] };
  const header = rows[0];
  if (!header) return result;
  const index: Partial<Record<Field, number>> = {};
  header.forEach((h, i) => {
    const key = h.trim().toLowerCase();
    const field = (Object.keys(HEADER_WORDS) as Field[]).find((f) => index[f] === undefined && HEADER_WORDS[f].includes(key));
    if (field) {
      index[field] = i;
      result.recognised.push(h.trim());
    } else result.ignored.push(h.trim());
  });
  if (index.number === undefined) result.missing.push("Rechnungsnummer");
  if (index.issueDate === undefined) result.missing.push("Rechnungsdatum");
  if (index.gross === undefined) result.missing.push("Brutto-Betrag");
  if (index.customerNumber === undefined && index.customerName === undefined) result.missing.push("Kundennummer oder Kundenname");
  if (result.missing.length) return result;
  const cell = (r: string[], f: Field) => (index[f] === undefined ? "" : (r[index[f]!] ?? "").trim());
  rows.slice(1).forEach((r, i) => {
    const line = i + 2;
    const number = cell(r, "number");
    const issueDate = isoDateFromText(cell(r, "issueDate"));
    const gross = centsFromText(cell(r, "gross"));
    if (!number) return void result.skipped.push({ line, reason: "keine Rechnungsnummer" });
    if (!issueDate) return void result.skipped.push({ line, reason: `${number}: Datum nicht lesbar` });
    if (gross === null || gross <= 0) return void result.skipped.push({ line, reason: `${number}: Betrag fehlt oder ist nicht positiv (Gutschriften bitte einzeln erfassen)` });
    const netRaw = index.net !== undefined ? centsFromText(cell(r, "net")) : null;
    const status = cell(r, "status").toLowerCase();
    const paidOn = isoDateFromText(cell(r, "paidOn"));
    const paid = /^(bezahlt|beglichen|paid|erledigt)/.test(status) || (status === "" && paidOn !== null);
    const custNo = cell(r, "customerNumber").replace(/\D/g, "");
    result.invoices.push({
      line,
      invoice: {
        number,
        issueDate,
        dueDate: isoDateFromText(cell(r, "dueDate")),
        customerNumber: custNo ? Number(custNo) : null,
        customerName: cell(r, "customerName") || null,
        netCents: netRaw !== null && netRaw > 0 ? netRaw : gross,
        grossCents: gross,
        paid,
        paidOn: paid ? paidOn ?? issueDate : null,
        subject: cell(r, "subject") || null,
      },
    });
  });
  return result;
}
